/**
 * The strategic vocabulary the query enhancer works from.
 *
 * Split out of `../query-enhancer.ts`. This is tuning DATA, not logic: the words
 * here decide which documents an embedding lookup can reach, so the useful
 * change is usually to this table and nothing else. Keeping it in its own module
 * makes that a one-file diff instead of an edit buried in string assembly.
 */

/**
 * Strategic context keywords for embedding retrieval
 */
export const CONTEXT_KEYWORDS = {
  // Primary identity and platform
  meridian: 'Meridian',
  wtc: 'West Tennessee Consulting',
  hub: 'WTC Hub',

  // Core focus areas
  salesSupport: 'sales support',
  operationsOptimization: 'operations optimization',
  ticketManagement: 'ticket management',
  mspOperations: 'MSP operations',

  // CRM entities
  contacts: 'contacts',
  companies: 'companies',
  deals: 'deals',
  tickets: 'support tickets',
  timeEntries: 'time entries',

  // User context
  teamMember: 'WTC team member',
  salesPipeline: 'sales pipeline',
  customerSupport: 'customer support',

  // Key outcomes
  dealTracking: 'deal tracking',
  pipelineManagement: 'pipeline management',
  ticketResolution: 'ticket resolution',
  processImprovement: 'process improvement',

  // Expert context
  actionableGuidance: 'actionable guidance',
  bestPractices: 'best practices',
  mspBestPractices: 'MSP best practices',

  // Hub integrations
  quickbooks: 'QuickBooks',
  crmLookup: 'CRM lookup',
  logCommunication: 'log communication',
};

/** The topics `detectQueryTopic` can return. */
export type QueryTopic = 'sales' | 'operations' | 'tickets' | 'contacts' | 'deals' | 'quickbooks';

/**
 * Topic-specific keyword mappings for query expansion.
 *
 * ORDER MATTERS inside each list: `enhanceQueryWithContext` uses the first two
 * and `buildQueryVariations` uses the first one, so the leading entries are the
 * ones that actually reach an embedding.
 */
export const TOPIC_KEYWORDS: Record<string, string[]> = {
  sales: [
    'sales support',
    'deal tracking',
    'pipeline management',
    'customer insights',
    'follow-up leads',
  ],
  operations: [
    'operations optimization',
    'process improvement',
    'workflow guidance',
    'MSP operations',
    'resource allocation',
  ],
  tickets: [
    'ticket management',
    'support tickets',
    'ticket resolution',
    'escalation guidance',
    'customer support',
  ],
  contacts: [
    'contacts',
    'CRM contacts',
    'log communication',
    'meetings calls emails',
    'activity timeline',
  ],
  deals: [
    'deals',
    'sales pipeline',
    'deal tracking',
    'create deal',
    'update deal',
  ],
  quickbooks: [
    'QuickBooks',
    'invoices',
    'customer balance',
    'billing',
  ],
};
